import { useState, useEffect, useCallback } from "react";
import { api, rupiah, waktu, apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout } from "@/erp/shell";
import { toast } from "sonner";
import { Eye, CheckCircle, Truck, PackageCheck, XCircle, Package } from "lucide-react";

const STATUS_MAP = {
  awaiting_payment: { label: "Menunggu Bayar", cls: "bg-yellow-100 text-yellow-800" },
  paid:             { label: "Sudah Dibayar",  cls: "bg-blue-100 text-blue-800" },
  confirmed:        { label: "Dikonfirmasi",   cls: "bg-green-100 text-green-800" },
  shipped:          { label: "Dikirim",        cls: "bg-indigo-100 text-indigo-800" },
  delivered:        { label: "Diterima",       cls: "bg-emerald-100 text-emerald-800" },
  cancelled:        { label: "Dibatalkan",     cls: "bg-red-100 text-red-800" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export default function TokoAdmin() {
  const { can } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shop/orders");
      setOrders(data);
    } catch { toast.error("Gagal memuat pesanan"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const action = async (oid, endpoint, label) => {
    try {
      await api.post(`/shop/orders/${oid}/${endpoint}`);
      toast.success(label);
      load();
      if (detail?.id === oid) {
        const fresh = orders.find((o) => o.id === oid);
        if (fresh) setDetail(fresh);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Toko Online" breadcrumb={["Toko Online"]}>
      {/* Filter status */}
      <div className="mb-5 flex flex-wrap gap-2">
        {[["all", "Semua"], ...Object.entries(STATUS_MAP).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "border-[#8C6D1F] bg-[#8C6D1F] text-white" : "border-[#E5DEC9] text-[#4A4844] hover:border-[#8C6D1F]"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Stats strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Semua Pesanan", orders.length],
          ["Menunggu Bayar", orders.filter((o) => o.status === "awaiting_payment").length],
          ["Perlu Konfirmasi", orders.filter((o) => o.status === "paid").length],
          ["Dikirim", orders.filter((o) => o.status === "shipped").length],
        ].map(([lbl, val]) => (
          <div key={lbl} className="rounded-xl border border-[#E5DEC9] bg-white p-4">
            <p className="text-xs text-[#8A867C]">{lbl}</p>
            <p className="mt-1 text-2xl font-bold text-[#141414]">{val}</p>
          </div>
        ))}
      </div>

      {/* Tabel */}
      <div className="overflow-hidden rounded-xl border border-[#E5DEC9] bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5DEC9] bg-[#F4EFE6]">
            <tr>
              {["No. Order", "Pemesan", "Referral", "Total", "Status", "Tanggal", "Aksi"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#4A4844]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-[#8A867C]">Memuat...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-[#8A867C]">Tidak ada pesanan.</td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-[#F4EFE6] hover:bg-[#FDFAF5]" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-[#141414]">{o.order_number}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#141414]">{o.recipient_name}</div>
                  <div className="text-xs text-[#8A867C]">{o.recipient_phone}</div>
                </td>
                <td className="px-4 py-3" data-testid={`order-referral-${o.id}`}>
                  {o.partner_snapshot ? (
                    <div>
                      <div className="text-xs font-semibold text-[#141414]">{o.partner_snapshot.referral_code}</div>
                      <div className="text-xs text-[#8A867C]">{o.partner_snapshot.consultant_name || o.partner_snapshot.partner_name}</div>
                    </div>
                  ) : <span className="text-xs text-[#8A867C]">—</span>}
                </td>
                <td className="px-4 py-3 font-semibold">{rupiah(o.total_amount)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3 text-xs text-[#8A867C]">{waktu(o.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => setDetail(o)}
                      data-testid={`order-detail-${o.id}`}
                      className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]"
                      title="Lihat detail"><Eye size={13} /></button>
                    {can("booking.confirm") && o.status === "paid" && (
                      <button onClick={() => action(o.id, "confirm", "Order dikonfirmasi")}
                        data-testid={`order-confirm-${o.id}`}
                        className="rounded-lg border border-green-200 p-1.5 text-green-700 hover:bg-green-50"
                        title="Konfirmasi"><CheckCircle size={13} /></button>
                    )}
                    {can("booking.confirm") && o.status === "confirmed" && (
                      <button onClick={() => action(o.id, "ship", "Pesanan dikirim")}
                        data-testid={`order-ship-${o.id}`}
                        className="rounded-lg border border-indigo-200 p-1.5 text-indigo-700 hover:bg-indigo-50"
                        title="Kirim"><Truck size={13} /></button>
                    )}
                    {can("booking.confirm") && o.status === "shipped" && (
                      <button onClick={() => action(o.id, "deliver", "Pesanan diterima")}
                        data-testid={`order-deliver-${o.id}`}
                        className="rounded-lg border border-emerald-200 p-1.5 text-emerald-700 hover:bg-emerald-50"
                        title="Tandai Diterima"><PackageCheck size={13} /></button>
                    )}
                    {can("booking.confirm") && !["cancelled", "delivered"].includes(o.status) && (
                      <button onClick={() => { if (window.confirm("Batalkan order ini?")) action(o.id, "cancel", "Order dibatalkan"); }}
                        data-testid={`order-cancel-${o.id}`}
                        className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C] hover:bg-red-50"
                        title="Batalkan"><XCircle size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Detail */}
      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} onAction={async (ep, label) => { await action(detail.id, ep, label); const { data } = await api.get("/shop/orders"); setOrders(data); const updated = data.find((o) => o.id === detail.id); if (updated) setDetail(updated); }} />}
    </ErpLayout>
  );
}

function OrderDetail({ order: o, onClose, onAction }) {
  const { can } = useAuth();
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6" data-testid="order-detail-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#8C6D1F]">Pesanan Toko</p>
            <h2 className="mt-0.5 font-display text-xl font-bold text-[#141414]">{o.order_number}</h2>
          </div>
          <button onClick={onClose} className="text-[#8A867C] hover:text-[#141414]"><XCircle size={18} /></button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <StatusBadge status={o.status} />
          <span className="text-xs text-[#8A867C]">{waktu(o.created_at)}</span>
        </div>

        {/* Pemesan */}
        <div className="mt-4 rounded-xl bg-[#F4EFE6] p-4 text-sm">
          <p className="font-semibold text-[#141414]">{o.recipient_name}</p>
          <p className="text-[#4A4844]">{o.recipient_phone}</p>
          <p className="mt-1 text-[#4A4844]">{o.shipping_address}, {o.shipping_city} {o.shipping_postal_code}</p>
          {o.note && <p className="mt-1 italic text-[#8A867C]">Catatan: {o.note}</p>}
        </div>

        {/* Items */}
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#8A867C]">Item Pesanan</p>
          {(o.items || []).map((it, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-[#E5DEC9] p-3 text-sm">
              <div className="flex items-center gap-3">
                <Package size={14} className="text-[#8A867C]" />
                <div>
                  <p className="font-medium text-[#141414]">{it.name}</p>
                  <p className="text-xs text-[#8A867C]">{it.qty} × {rupiah(it.unit_price)}</p>
                </div>
              </div>
              <p className="font-semibold">{rupiah(it.amount)}</p>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mt-3 flex justify-between rounded-lg bg-[#141414] px-4 py-3 text-sm text-white">
          <span className="font-semibold">Total</span>
          <span className="font-bold">{rupiah(o.total_amount)}</span>
        </div>

        {o.invoice_number && (
          <p className="mt-2 text-xs text-[#8A867C]">Invoice: <span className="font-mono font-semibold">{o.invoice_number}</span></p>
        )}

        {o.partner_snapshot ? (
          <div className="mt-4 rounded-xl bg-[#FAF7F0] p-4 text-sm" data-testid="order-detail-referral">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#8C6D1F]">Referral Mitra</p>
            <p className="mt-1 text-[#141414]"><b>{o.partner_snapshot.consultant_name || o.partner_snapshot.partner_name}</b> ({o.partner_snapshot.referral_code})</p>
            <p className="text-xs text-[#4A4844]">{o.partner_snapshot.branch_name}{o.partner_snapshot.subbranch_name ? ` · ${o.partner_snapshot.subbranch_name}` : ""}</p>
            <p className="mt-1 text-xs text-[#8A867C]">Pool komisi order: {rupiah(o.commission_pool_total || 0)}</p>
          </div>
        ) : (
          <div className="mt-4 rounded-xl bg-[#F4EFE6] p-3 text-sm text-[#8C6D1F]" data-testid="order-detail-referral-empty">Tanpa referral mitra.</div>
        )}

        {/* Aksi */}
        {can("booking.confirm") && (
          <div className="mt-5 flex flex-wrap gap-2">
            {o.status === "paid" && (
              <button onClick={() => onAction("confirm", "Order dikonfirmasi")}
                data-testid="modal-confirm-btn"
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700">
                <CheckCircle size={14} /> Konfirmasi
              </button>
            )}
            {o.status === "confirmed" && (
              <button onClick={() => onAction("ship", "Pesanan dikirim")}
                data-testid="modal-ship-btn"
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                <Truck size={14} /> Kirim Pesanan
              </button>
            )}
            {o.status === "shipped" && (
              <button onClick={() => onAction("deliver", "Pesanan diterima")}
                data-testid="modal-deliver-btn"
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                <PackageCheck size={14} /> Tandai Diterima
              </button>
            )}
            {!["cancelled", "delivered"].includes(o.status) && (
              <button onClick={() => { if (window.confirm("Batalkan order ini?")) onAction("cancel", "Order dibatalkan"); }}
                data-testid="modal-cancel-btn"
                className="flex items-center gap-1.5 rounded-lg border border-[#F0C4C4] px-4 py-2 text-sm font-semibold text-[#B91C1C] hover:bg-red-50">
                <XCircle size={14} /> Batalkan
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
